import { List, Map } from 'immutable'
import { KeyboardEvent } from 'react'
// @ts-ignore
import { protocol, envSuffix } from 'app/config'

import { EMPLOYEE, PEER, REVIEWS_TYPES, REVIEWS_USER_TYPES, SUPERIOR, TYPE_OF_REVIEWERS } from 'app/constants/reviews'
import { REVIEWS_SURVEY_PATH } from '../app/constants/navLinks'

export const FIRST_NAME = '<@UserFirstName>'
export const LAST_NAME = '<@UserLastName>'
export const REVIEWER_FIRST_NAME = '<@ReviewerFirstName>'
export const REVIEWER_LAST_NAME = '<@ReviewerLastName>'

export const prepareReviewers = (reviewersArray: any) =>
  reviewersArray.reduce(
    (reduction: any, reviewer: any) =>
      reduction.push(
        Map({
          firstName: reviewer.get('firstName'),
          lastName: reviewer.get('lastName'),
          email: reviewer.get('email'),
          isComplete: reviewer.get('isComplete'),
          isMemberNotSavedYet: reviewer.get('isMemberNotSavedYet'),
        })
      ),
    List()
  )

export const countCompletedReviews = (reviewersArray: any) =>
  reviewersArray.reduce((reduction: any, reviewer: any) => (reviewer.get('isComplete') ? reduction + 1 : reduction), 0)

export const prepareReviewerDataBeforeSend = (user: any, reviewer: any, reviewType: any, reviewerType: any) => ({
  participantEmail: user.get('email'),
  participantFirstName: user.get('firstName'),
  participantLastName: user.get('lastName'),
  reviewerEmail: reviewer.get('email'),
  reviewerFirstName: reviewer.get('firstName'),
  reviewerLastName: reviewer.get('lastName'),
  reviewType,
  reviewerType: reviewerType.toLowerCase(),
})

export const prepareReviewerDataForReviewersReminder = (list: any, user: any, reviewType: any, reviewerType: any) => {
  return list.reduce((accumulator: any, reviewer: any) => {
    if (reviewer.get('isComplete') === false) {
      accumulator.push(prepareReviewerDataBeforeSend(user, reviewer, reviewType, reviewerType))
    }
    return accumulator
  }, [])
}

export const prepareUserDataForReviewersReminder = (user: any, reviewType: any) => {
  const superiors = prepareReviewerDataForReviewersReminder(
    user.get(`${reviewType}${SUPERIOR}`),
    user,
    reviewType,
    SUPERIOR.toLowerCase()
  )
  const peers = prepareReviewerDataForReviewersReminder(
    user.get(`${reviewType}${PEER}`),
    user,
    reviewType,
    PEER.toLowerCase()
  )
  const employees = prepareReviewerDataForReviewersReminder(
    user.get(`${reviewType}${EMPLOYEE}`),
    user,
    reviewType,
    EMPLOYEE.toLowerCase()
  )
  return [...superiors, ...peers, ...employees]
}

export const prepareUserDataForCreateReportBulk = (listReviewsUsers: List<any>) => {
  return listReviewsUsers.reduce((accomulator: any, reviewsUser: any) => {
    accomulator.push(reviewsUser.get('email'))
    return accomulator
  }, [])
}

export const createLinkForReport = (
  organizationId: string | number,
  userId: string | number,
  reviewType: string,
  origin: string
) => {
  return `${origin}/reports?hash=${btoa(`organizationId=${organizationId}&userId=${userId}&reviewType=${reviewType}`)}`
}

export const createLinkForSelfReviewsSurvey = (survey: Map<string, any>, selectedOrganizationId: number | null) => {
  let basedUrl = `${protocol}${survey.getIn(['usedBy', 'domain'])}${envSuffix}.prohabits.com`
  if (origin.includes('localhost')) {
    basedUrl = origin
  }
  return `${basedUrl}${REVIEWS_SURVEY_PATH}?reviews_survey_id=${survey.get(
    'id'
  )}&time=1&organization_id=${selectedOrganizationId}`
}

export const shouldDisableAbilityToResend = (reviewType: any, user: any) => {
  if (reviewType === REVIEWS_TYPES.T1) {
    return user.get('t1ReportCreated') || !user.get('t1Self')
  }
  if (reviewType === REVIEWS_TYPES.T2) {
    return user.get('t2ReportCreated') || !user.get('t2Self')
  }
}

export const parseMinAndMaxFieldsReviewers = (data: any) => {
  data[`${TYPE_OF_REVIEWERS.SUPERIOR}Min`] = parseInt(data[`${TYPE_OF_REVIEWERS.SUPERIOR}Min`], 10)
  data[`${TYPE_OF_REVIEWERS.SUPERIOR}Max`] = parseInt(data[`${TYPE_OF_REVIEWERS.SUPERIOR}Max`], 10)
  data[`${TYPE_OF_REVIEWERS.EMPLOYEE}Min`] = parseInt(data[`${TYPE_OF_REVIEWERS.EMPLOYEE}Min`], 10)
  data[`${TYPE_OF_REVIEWERS.EMPLOYEE}Max`] = parseInt(data[`${TYPE_OF_REVIEWERS.EMPLOYEE}Max`], 10)
  data[`${TYPE_OF_REVIEWERS.PEER}Min`] = parseInt(data[`${TYPE_OF_REVIEWERS.PEER}Min`], 10)
  data[`${TYPE_OF_REVIEWERS.PEER}Max`] = parseInt(data[`${TYPE_OF_REVIEWERS.PEER}Max`], 10)
  return data
}

export const substituteParticipantFirstNameAndLastName = (
  string: string,
  type: REVIEWS_USER_TYPES = REVIEWS_USER_TYPES.USER,
  email: string,
  firstName?: string,
  lastName?: string
) => {
  if (!string) {
    return ''
  }
  const FN = type === REVIEWS_USER_TYPES.REVIEWER ? REVIEWER_FIRST_NAME : FIRST_NAME
  const LN = type === REVIEWS_USER_TYPES.REVIEWER ? REVIEWER_LAST_NAME : LAST_NAME
  const firstNameReg = new RegExp(FN, 'gi')
  const lastNameReg = new RegExp(LN, 'gi')
  if (firstName && lastName) {
    return string.replace(firstNameReg, firstName).replace(lastNameReg, lastName)
  }
  if (firstName && !lastName) {
    return string.replace(new RegExp(`${FN} ${FN}|${LN}`, 'gi'), email).replace(firstNameReg, firstName)
  }
  if (!firstName && lastName) {
    return string.replace(new RegExp(`${FN} ${FN}|${FN}`, 'gi'), email).replace(lastNameReg, lastName)
  }
  return string.replace(new RegExp(`${FN} ${FN}|${FN}|${LN}`, 'gi'), email)
}

export const handlePressEnter = (action: () => void, hasAccess: boolean) => (e: KeyboardEvent<any>) => {
  const pressedKey = e.keyCode || e.which
  // 13 = 'enter'
  if (pressedKey === 13 && hasAccess) {
    action()
  }
}

export const trimmed = (value: string) => {
  if (value.trim().length > 0) {
    return value
  }
  return ''
}
